import React, { useEffect, useState } from 'react';
import { getSharedValuation } from '../api/share';
import { productName, productTagline, logoSrc, logoStyleCompact } from '../brand';
import PageFooter from '../components/chrome/PageFooter';
import Tier from '../components/Tier';
import RevenueModels from '../components/RevenueModels';
import PerLife from '../components/PerLife';
import Disclaimer from '../components/Disclaimer';
import { describeSharedValuationError } from '../lib/describeError';
import { formatUSD } from '../lib/format';

// Public read-only view at /share/{token}. Rendered OUTSIDE the AppShell
// (index.js short-circuits to this component when window.location starts
// with /share/), so no nav, no auth context, no edit / save / share
// affordances per FR-029.
export default function SharedValuation({ token }) {
  const [state, setState] = useState({ status: 'loading' });

  useEffect(() => {
    if (!token) {
      setState({ status: 'error', code: 'missing_token' });
      return;
    }
    let cancelled = false;
    getSharedValuation(token)
      .then((data) => { if (!cancelled) setState({ status: 'ok', data }); })
      .catch((err) => { if (!cancelled) setState({ status: 'error', code: err?.code || 'load_failed' }); });
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div style={pageStyle}>
      <header style={headerStyle}>
        <img src={logoSrc} alt={productName} style={logoStyleCompact} />
        <div style={taglineStyle}>{productTagline}</div>
      </header>

      <main style={mainStyle}>
        {state.status === 'loading' && (
          <p style={{ color: 'var(--muted)', textAlign: 'center', padding: '40px' }}>Loading shared valuation…</p>
        )}

        {state.status === 'error' && (() => {
          const { title, body } = describeSharedValuationError(state.code);
          return (
            <div style={errorBoxStyle}>
              <h1 style={errorTitleStyle}>{title}</h1>
              <p style={errorBodyStyle}>{body}</p>
            </div>
          );
        })()}

        {state.status === 'ok' && <Snapshot data={state.data} />}
      </main>

      <PageFooter finePrint={`Shared via ${productName} · Read-only · Estimates only`} />
    </div>
  );
}

function Snapshot({ data }) {
  const result = data.outputs || {};
  const inputs = data.inputs || {};

  return (
    <article style={cardStyle}>
      <div style={eyebrowStyle}>Shared estimate</div>
      <h1 style={titleStyle}>{data.name}</h1>
      <div style={metaStyle}>
        Snapshot generated {formatDate(data.snapshot_at)}
      </div>

      <div style={{ marginTop: '32px' }}>
        <Tier tier={result.valueCategory} />
      </div>

      <div style={{ marginTop: '20px' }}>
        <RevenueModels
          pharmaAnnual={result.pharmaAnnual}
          aiMonthly={result.aiMonthly}
          consortiumPerInst={result.consortiumPerInst}
        />
      </div>

      <div style={{ marginTop: '12px' }}>
        <PerLife pharmaAnnual={result.pharmaAnnual} samples={inputs.samples} />
      </div>

      <div style={summaryStyle}>
        <div style={summaryRowStyle}>
          <span style={summaryLabelStyle}>Three years of base license fees (estimate)</span>
          <span style={summaryValueStyle}>{formatUSD(result.threeYearValue)}</span>
        </div>
      </div>

      <Disclaimer />

      {data.disclaimer && data.disclaimer !== '' && data.disclaimer.indexOf('Estimates only') === -1 && (
        <p style={{ marginTop: '8px', fontSize: '12px', color: 'var(--muted)', fontStyle: 'italic' }}>{data.disclaimer}</p>
      )}
    </article>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

const pageStyle = { minHeight: '100vh', background: 'var(--bg)', color: 'var(--ink)', fontFamily: 'var(--font)', display: 'flex', flexDirection: 'column' };
const headerStyle = {
  padding: '20px 48px',
  borderBottom: '1px solid var(--line)',
  background: 'var(--bg)',
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
};
const taglineStyle = { fontSize: '12px', color: 'var(--muted)' };

const mainStyle = { padding: '48px 48px 24px', maxWidth: '960px', margin: '0 auto', width: '100%', flex: 1 };

const cardStyle = { background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: '8px', padding: '36px 44px' };
const eyebrowStyle = { fontFamily: 'var(--display)', fontStyle: 'italic', fontSize: '14px', color: 'var(--warm)' };
const titleStyle = { fontFamily: 'var(--display)', fontWeight: 500, fontSize: '36px', letterSpacing: '-0.02em', margin: '8px 0 0', lineHeight: 1.05, color: 'var(--ink)' };
const metaStyle = { fontSize: '13px', color: 'var(--ink2)', marginTop: '14px', lineHeight: 1.55 };

const summaryStyle = { background: 'var(--accent-bg)', border: '1px solid var(--accent)', borderRadius: '8px', padding: '12px 16px', marginTop: '12px' };
const summaryRowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' };
const summaryLabelStyle = { fontSize: '12px', color: 'var(--accent)', fontWeight: 600 };
const summaryValueStyle = { fontFamily: 'var(--display)', fontStyle: 'italic', fontSize: '20px', fontWeight: 500, color: 'var(--accent)' };

const errorBoxStyle = { padding: '40px', textAlign: 'center', background: 'var(--paper)', border: '1px dashed var(--muted2)', borderRadius: '8px' };
const errorTitleStyle = { fontFamily: 'var(--display)', fontWeight: 500, fontSize: '24px', color: 'var(--ink)', margin: 0 };
const errorBodyStyle = { fontSize: '14px', color: 'var(--ink2)', marginTop: '14px', lineHeight: 1.55, maxWidth: '500px', marginLeft: 'auto', marginRight: 'auto' };

